import { useState } from "react"
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import { ArrowDown, Check, Hand, ShieldCheck } from "lucide-react"
import { toast } from "sonner"
import type { ConversationSummary } from "@vienna/contracts"
import { PERMISSION_LABELS } from "@vienna/contracts"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { PageHeading } from "@/components/app/page-heading"
import { api, ApiError } from "@/lib/api"
import { formatDateTime } from "@/lib/format"
import { channelsQuery, conversationQuery } from "@/lib/queries"
import { useWorkspaceContext } from "@/lib/session"

export const Route = createFileRoute("/w/$workspace/release/$conversationId")({
  loader: ({ context, params }) => Promise.all([context.queryClient.ensureQueryData(conversationQuery(params.workspace, params.conversationId)), context.queryClient.ensureQueryData(channelsQuery(params.workspace))]),
  component: Release,
})

/** Screen 55. Without an active journey, release makes the conversation unowned rather than pretending to resume. */
function Release() {
  const { workspace } = useWorkspaceContext()
  const { conversationId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const detail = useSuspenseQuery(conversationQuery(workspace.slug, conversationId)).data
  const channels = useSuspenseQuery(channelsQuery(workspace.slug)).data
  const [confirm, setConfirm] = useState(false)
  const params = { workspace: workspace.slug }
  const { conversation, contact, activeRun } = detail
  const connection = channels.connections.find((c) => c.provider === conversation.provider)
  const lastHumanMessage = [...detail.items].reverse().find((i) => i.kind === "message" && i.direction === "outbound" && i.authorType === "member")
  const lastAutomated = [...detail.items].reverse().find((i) => i.kind === "message" && i.authorType === "automation")
  const windowOpen = contact.supportWindowEndsAt ? new Date(contact.supportWindowEndsAt).getTime() > new Date(conversation.updatedAt).getTime() : false
  const release = useMutation({
    mutationFn: () => api<ConversationSummary>(`/w/${workspace.slug}/conversations/${conversationId}/release`, { method: "POST", body: JSON.stringify({ confirm: true }) }),
    onSuccess: async (c) => {
      await queryClient.invalidateQueries({ queryKey: ["workspace", workspace.slug] })
      toast.success(c.controlMode === "automation" ? "Conversation released to its journey" : "Conversation released")
      await navigate({ to: "/w/$workspace/inbox", params, search: { c: conversationId } })
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.problem.title : "Could not release the conversation"),
  })

  return (
    <div className="page-wrap">
      <PageHeading title="Hand the conversation back" description={activeRun ? `Review exactly where ${activeRun.automationName} will continue for ${contact.displayName}.` : `Review what happens when your team stops owning ${contact.displayName}'s conversation.`}>
        <Badge variant="secondary">{activeRun ? activeRun.version : "No active journey"}</Badge>
      </PageHeading>
      <div className="release-layout">
        <Card>
          <CardHeader>
            <CardTitle>{activeRun ? "Continue from the pause point" : "Where things stand"}</CardTitle>
            <CardDescription className="text-xs">Completed messages are preserved.</CardDescription>
          </CardHeader>
          <CardContent>
            <ol className="release-timeline">
              {[
                { icon: <Check size={16} />, label: "Last completed step", title: lastAutomated ? "Automated message sent" : lastHumanMessage ? "Your team replied" : "Conversation received", body: lastAutomated ? `Sent ${formatDateTime(lastAutomated.occurredAt)}. This message will not be sent again.` : lastHumanMessage ? `Sent ${formatDateTime(lastHumanMessage.occurredAt)}.` : "No outbound messages yet." },
                { icon: <Hand size={16} />, label: "Current pause point", title: "Human ownership", body: `${conversation.assigneeName ?? "Your team"} owns the conversation. Automation stays paused until release.` },
                { icon: <ArrowDown size={16} />, label: "Next step after release", title: activeRun ? activeRun.stepLabel : "Unowned, ready for the next reply", body: activeRun ? "Continue on the configured branch after handover." : "No journey is attached. The next inbound message routes like any new one; a teammate can take over again at any time.", next: true },
              ].map((step) => (
                <li key={step.label}>
                  <span className={`timeline-marker ${step.next ? "next" : ""}`}>{step.icon}</span>
                  <div>
                    <span className="text-[10px] tracking-wider text-muted-foreground uppercase">{step.label}</span>
                    <h3 className="mt-1.5 mb-2 text-base font-medium">{step.title}</h3>
                    <p className="text-xs leading-5 text-muted-foreground">{step.body}</p>
                  </div>
                </li>
              ))}
            </ol>
            <div className="core-hint">
              <ShieldCheck className="mt-0.5 size-4 shrink-0" />
              <div>Releasing resumes an existing run. It never restarts a journey or resends completed messages.</div>
            </div>
          </CardContent>
        </Card>
        <div className="space-y-5">
          <Card>
            <CardHeader>
              <CardTitle>Ready to continue?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {[
                ["Channel connection", connection ? `${connection.provider === "instagram" ? "Instagram" : "Messenger"} is ${connection.status === "connected" ? "available" : connection.status.replace("_", " ")}` : "No connection found"],
                ["Recipient eligibility", windowOpen ? "Available now · checked again at send" : "Support window closed · rechecked at send"],
                ["Marketing consent", contact.marketing === "granted" ? "Recorded · marketing steps allowed" : `${PERMISSION_LABELS[contact.marketing]} · marketing steps stay blocked`],
                ["Journey version", activeRun ? `Existing run continues on ${activeRun.version}` : "No active run"],
              ].map(([label, body]) => (
                <div key={label} className="flex gap-3">
                  <ShieldCheck className="mt-0.5 size-4 text-muted-foreground" />
                  <div>
                    <h3 className="text-xs font-medium">{label}</h3>
                    <p className="mt-1 text-[11px] leading-5 text-muted-foreground">{body}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="space-y-5">
              <label className="flex items-start gap-3 text-xs leading-5">
                <Checkbox checked={confirm} onCheckedChange={(v) => setConfirm(v === true)} />
                Release human ownership{activeRun ? " and resume this run" : ""}.
              </label>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" asChild>
                  <Link to="/w/$workspace/inbox" params={params} search={{ c: conversationId }}>
                    Keep paused
                  </Link>
                </Button>
                <Button className="flex-1" disabled={!confirm || release.isPending} onClick={() => release.mutate()}>
                  {activeRun ? "Release & resume" : "Release"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
